import { useEffect, useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  Check,
  Pencil,
  Users,
  X,
  UserPlus,
  UserMinus,
} from "lucide-react";
import { toast } from "react-toastify";
import { scheduleDeferredDeleteWithUndo } from "@/lib/deferredDeleteWithUndo";
import {
  accessService,
  type Group,
  type GroupMember,
  type Role,
} from "@/services/accessService";
import { usersService } from "@/services/usersService";
import type { UserSummary } from "@/types/entities";
import { useSettingsStore } from "@/store/settingsStore";
import { useUiStore } from "@/store/uiStore";

function memberLabel(member: GroupMember): string {
  const name = [member.firstName, member.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (member.email) return member.email;
  if (member.username) return member.username;
  return "(Unknown user)";
}

export function GroupsTab() {
  const { legalEntityId } = useSettingsStore();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const { startPageLoading, stopPageLoading } = useUiStore();

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalGroupId, setModalGroupId] = useState<string | null>(null);
  const [modalName, setModalName] = useState("");
  const [modalDesc, setModalDesc] = useState("");
  const [modalSaving, setModalSaving] = useState(false);
  const [modalRoles, setModalRoles] = useState<string[]>([]);
  const [allRoles, setAllRoles] = useState<Role[]>([]);

  const [membersGroupId, setMembersGroupId] = useState<string | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [searchResults, setSearchResults] = useState<UserSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingUser, setAddingUser] = useState<string | null>(null);

  const [confirmState, setConfirmState] = useState<{
    message: string;
    onConfirm: () => void;
  } | null>(null);

  const load = () =>
    accessService
      .listGroups()
      .then(setGroups)
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    accessService.listRoles().then(setAllRoles);
  }, []);

  const isEditing = modalGroupId !== null;

  const openCreateModal = () => {
    setModalGroupId(null);
    setModalName("");
    setModalDesc("");
    setModalRoles([]);
    setModalOpen(true);
  };

  const openEditModal = async (g: Group) => {
    setModalGroupId(g.id);
    setModalName(g.name);
    setModalDesc(g.description ?? "");
    setModalRoles([]);
    setModalOpen(true);
    const roles = await accessService.getGroupRoles(g.id);
    setModalRoles(roles);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalGroupId(null);
    setModalName("");
    setModalDesc("");
    setModalRoles([]);
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (confirmState) setConfirmState(null);
      else if (membersGroupId) closeMembers();
      else if (modalOpen) closeModal();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [modalOpen, membersGroupId, confirmState]);

  const handleModalSave = async () => {
    if (!modalName.trim()) return;
    setModalSaving(true);
    try {
      if (isEditing) {
        const updated = await accessService.updateGroup(modalGroupId, {
          name: modalName.trim(),
          description: modalDesc.trim() || undefined,
        });
        await accessService.setGroupRoles(modalGroupId, modalRoles);
        setGroups((prev) => prev.map((g) => (g.id === modalGroupId ? updated : g)));
        toast.success( `Group "${modalName.trim()}" updated successfully`);
      } else {
        const group = await accessService.createGroup({
          name: modalName.trim(),
          description: modalDesc.trim() || undefined,
          legalEntityId: legalEntityId ?? undefined,
          roles: modalRoles,
        });
        setGroups((prev) => [...prev, group]);
        toast.success( `Group "${modalName.trim()}" created successfully`);
      }
      closeModal();
    } catch {
      toast.error( `Failed to ${isEditing ? "update" : "create"} group`);
    } finally {
      setModalSaving(false);
    }
  };

  const handleDelete = (id: string, name: string) => {
    const snapshot = groups.find((g) => g.id === id);
    if (!snapshot) return;

    const hideFromList = () => {
      setGroups((prev) => prev.filter((g) => g.id !== id));
      setMembersGroupId((openId) => {
        if (openId === id) {
          setMembers([]);
          setUserSearch("");
          setSearchResults([]);
          return null;
        }
        return openId;
      });
    };

    const restoreToList = () => {
      setGroups((prev) => (prev.some((g) => g.id === id) ? prev : [...prev, snapshot]));
    };

    scheduleDeferredDeleteWithUndo({
      idKey: `group:${id}`,
      label: name,
      hint: "This group will be removed from Keycloak.",
      onScheduled: hideFromList,
      onUndo: restoreToList,
      onFailed: restoreToList,
      onCommit: () => accessService.deleteGroup(id),
      onComplete: () => toast.success(`Group "${name}" deleted successfully`),
    });
  };

  const openMembers = async (groupId: string) => {
    setMembersGroupId(groupId);
    setMembersLoading(true);
    setMembers([]);
    setUserSearch("");
    setSearchResults([]);
    try {
      const m = await accessService.getGroupMembers(groupId);
      setMembers(m);
    } finally {
      setMembersLoading(false);
    }
  };

  const closeMembers = () => {
    setMembersGroupId(null);
    setMembers([]);
    setUserSearch("");
    setSearchResults([]);
  };

  const handleSearchUsers = async () => {
    if (!userSearch.trim()) return;
    setSearching(true);
    try {
      const res = await usersService.list({ search: userSearch.trim() }, { page: 0, size: 10 });
      // The same person can be synced as several rows (different ids), so dedupe by a
      // stable identity key — email, else name — rather than id.
      const identityKey = (email?: string | null, name?: string | null, id?: string) =>
        (email || name || id || "").trim().toLowerCase();
      const memberKeys = new Set(
        members.map((m) => identityKey(m.email, [m.firstName, m.lastName].filter(Boolean).join(" "), m.id)),
      );
      const seen = new Set<string>();
      setSearchResults(
        (res.content ?? []).filter((u) => {
          const key = identityKey(u.email, u.name, u.id);
          if (seen.has(key) || memberKeys.has(key)) return false;
          seen.add(key);
          return true;
        }),
      );
    } finally {
      setSearching(false);
    }
  };

  const handleAddMember = async (userId: string) => {
    if (!membersGroupId) return;
    setAddingUser(userId);
    try {
      await accessService.addGroupMembers(membersGroupId, [userId]);
      const m = await accessService.getGroupMembers(membersGroupId);
      setMembers(m);
      setSearchResults((prev) => prev.filter((u) => u.id !== userId));
      toast.success("Member added successfully");
    } catch {
      toast.error("Failed to add member");
    } finally {
      setAddingUser(null);
    }
  };

  const handleRemoveMember = (userId: string) => {
    if (!membersGroupId) return;
    setConfirmState({
      message: "Remove this member from the group?",
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await accessService.removeGroupMember(membersGroupId, userId);
          setMembers((prev) => prev.filter((m) => m.id !== userId));
          toast.success("Member removed successfully");
        } catch {
          toast.error("Failed to remove member");
        }
      },
    });
  };

  if (loading) return <LoadingSpinner />;

  const membersGroup = groups.find((g) => g.id === membersGroupId);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {groups.length} group{groups.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 rounded-xl bg-bridge-accent px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-bridge-accent/90"
        >
          <Plus size={14} /> Create Group
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/80">
              <th className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                Name
              </th>
              <th className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                Description
              </th>
              <th className="px-5 py-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {groups.map((g) => (
              <tr
                key={g.id}
                className="transition-colors hover:bg-slate-50/70"
              >
                <td className="px-5 py-3.5 font-semibold text-slate-900">
                  {g.name}
                </td>
                <td className="px-5 py-3.5 text-slate-500">
                  {g.description || "—"}
                </td>
                <td className="px-5 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => openMembers(g.id)}
                      className="rounded-lg p-1.5 text-bridge-accent transition-colors hover:bg-bridge-accent/10"
                      title="Manage members"
                    >
                      <Users size={14} />
                    </button>
                    <button
                      onClick={() => openEditModal(g)}
                      className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                      title="Edit group"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(g.id, g.name)}
                      className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                      title="Delete group"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {groups.length === 0 && (
          <p className="py-12 text-center text-sm text-slate-400">
            No groups found.
          </p>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fadeIn"
            onClick={closeModal}
            aria-hidden
          />
          <div
            className="relative flex w-full max-w-md flex-col overflow-hidden rounded-lg border border-bridge-border/70 bg-bridge-surface shadow-glass"
            role="dialog"
            aria-labelledby="group-modal-title"
          >
            <header className="flex items-center justify-between gap-3 border-b border-bridge-border/60 px-5 py-4">
              <h2
                id="group-modal-title"
                className="text-base font-semibold text-bridge-fg"
              >
                {isEditing ? "Edit group" : "Create new group"}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface-hover hover:text-bridge-fg"
              >
                <X size={16} />
              </button>
            </header>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-bridge-fg mb-1">
                  Group name
                </label>
                <input
                  autoFocus
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && modalName.trim() && handleModalSave()
                  }
                  placeholder="e.g. Sales Team"
                  className="w-full px-3 py-2 text-sm border border-bridge-border rounded-lg focus:outline-none focus:ring-2 focus:ring-bridge-accent/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-bridge-fg mb-1">
                  Description
                </label>
                <input
                  value={modalDesc}
                  onChange={(e) => setModalDesc(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && modalName.trim() && handleModalSave()
                  }
                  placeholder="Optional description"
                  className="w-full px-3 py-2 text-sm border border-bridge-border rounded-lg focus:outline-none focus:ring-2 focus:ring-bridge-accent/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-bridge-fg mb-1">
                  Roles
                </label>
                {modalRoles.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {modalRoles.map((r) => (
                      <span
                        key={r}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-bridge-accent/10 text-bridge-accent"
                      >
                        {r}
                        <button
                          type="button"
                          onClick={() =>
                            setModalRoles((prev) =>
                              prev.filter((name) => name !== r),
                            )
                          }
                          className="p-0.5 rounded-full hover:bg-bridge-accent/20 transition-colors"
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      setModalRoles((prev) => [...prev, e.target.value]);
                    }
                  }}
                  className="w-full px-3 py-2 text-sm border border-bridge-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-bridge-accent/30"
                >
                  <option value="">Add a role...</option>
                  {allRoles
                    .filter((r) => !modalRoles.includes(r.name))
                    .map((r) => (
                      <option key={r.name} value={r.name}>
                        {r.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-bridge-border/60 px-5 py-3">
              <button
                onClick={closeModal}
                className="px-3 py-1.5 text-sm text-bridge-muted hover:text-bridge-fg rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleModalSave}
                disabled={modalSaving || !modalName.trim()}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-bridge-accent text-white text-sm font-medium rounded-lg disabled:opacity-50 hover:bg-bridge-accent/90 transition-colors"
              >
                {modalSaving ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Check size={13} />
                )}{" "}
                {isEditing ? "Update" : "Create"}
              </button>
            </footer>
          </div>
        </div>
      )}

      {membersGroupId && membersGroup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fadeIn"
            onClick={closeMembers}
            aria-hidden
          />
          <div
            className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-bridge-border/70 bg-bridge-surface shadow-glass"
            role="dialog"
            aria-labelledby="members-modal-title"
          >
            <header className="flex items-center justify-between gap-3 border-b border-bridge-border/60 px-5 py-4">
              <h2
                id="members-modal-title"
                className="text-base font-semibold text-bridge-fg"
              >
                Members of &ldquo;{membersGroup.name}&rdquo;
              </h2>
              <button
                type="button"
                onClick={closeMembers}
                className="rounded-md p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface-hover hover:text-bridge-fg"
              >
                <X size={16} />
              </button>
            </header>

            <div className="px-5 py-3 border-b border-bridge-border">
              <div className="flex gap-2">
                <input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchUsers()}
                  placeholder="Search users to add..."
                  className="flex-1 px-3 py-1.5 text-sm border border-bridge-border rounded-lg focus:outline-none focus:ring-2 focus:ring-bridge-accent/30"
                />
                <button
                  onClick={handleSearchUsers}
                  disabled={searching || !userSearch.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-bridge-accent text-white text-sm rounded-lg disabled:opacity-50 hover:bg-bridge-accent/90"
                >
                  {searching ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <UserPlus size={13} />
                  )}{" "}
                  Search
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto border border-bridge-border rounded-lg divide-y divide-bridge-border">
                  {searchResults.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between px-3 py-2 text-sm hover:bg-bridge-bg/40"
                    >
                      <span className="text-bridge-fg">
                        {u.name}{" "}
                        <span className="text-bridge-muted">({u.email})</span>
                      </span>
                      <button
                        onClick={() => handleAddMember(u.id)}
                        disabled={addingUser === u.id}
                        className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                      >
                        {addingUser === u.id ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <Plus size={13} />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3">
              {membersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2
                    size={20}
                    className="animate-spin text-bridge-accent"
                  />
                </div>
              ) : members.length === 0 ? (
                <p className="text-center text-bridge-muted text-sm py-8">
                  No members yet.
                </p>
              ) : (
                <div className="divide-y divide-bridge-border">
                  {members.map((m) => {
                    const label = memberLabel(m);
                    const showEmail = Boolean(m.email && label !== m.email);
                    const showId =
                      !m.email &&
                      !m.firstName &&
                      !m.lastName &&
                      !m.username;

                    return (
                      <div
                        key={m.id}
                        className="flex items-center justify-between py-2.5"
                      >
                        <div className="text-sm min-w-0">
                          <span className="font-medium text-bridge-fg">
                            {label}
                          </span>
                          {showEmail && (
                            <span className="text-bridge-muted ml-2">
                              {m.email}
                            </span>
                          )}
                          {showId && (
                            <span className="text-bridge-muted ml-2 font-mono text-xs">
                              {m.id}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveMember(m.id)}
                          disabled={!m.id}
                          className="p-1 text-red-400 hover:text-red-600 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                          title="Remove member"
                        >
                          <UserMinus size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmState && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fadeIn"
            onClick={() => setConfirmState(null)}
            aria-hidden
          />
          <div
            className="relative flex w-full max-w-sm flex-col overflow-hidden rounded-lg border border-bridge-border/70 bg-bridge-surface shadow-glass"
            role="alertdialog"
            aria-labelledby="confirm-dialog-title"
          >
            <header className="flex items-center gap-3 border-b border-bridge-border/60 px-5 py-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50">
                <Trash2 size={16} className="text-red-500" />
              </div>
              <h2
                id="confirm-dialog-title"
                className="text-base font-semibold text-bridge-fg"
              >
                Confirm
              </h2>
            </header>
            <div className="px-5 py-4">
              <p className="text-sm text-bridge-fg">{confirmState.message}</p>
            </div>
            <footer className="flex items-center justify-end gap-2 border-t border-bridge-border/60 px-5 py-3">
              <button
                onClick={() => setConfirmState(null)}
                className="px-3 py-1.5 text-sm text-bridge-muted hover:text-bridge-fg rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmState.onConfirm}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 size={24} className="animate-spin text-bridge-accent" />
    </div>
  );
}
